import type {
  AuthTokens,
  AuthenticatedUser,
  BusinessSettingsPayload,
  LoginResult,
  OrganizationContext,
  OrganizationSummary,
  PermissionCode,
} from '@stockpilot/shared';
import { baseApi, unwrap, unwrapList, type ListResult } from './baseApi';

// ---------------------------------------------------------------- domain shapes

export interface Branch {
  id: string;
  name: string;
  code: string;
  status: 'ACTIVE' | 'INACTIVE' | 'CLOSED';
  phone: string | null;
  email: string | null;
  city: string | null;
  isDefault: boolean;
  warehouses?: { id: string; name: string; code: string; isDefault: boolean }[];
  _count?: { warehouses: number; memberAccess: number };
}

export interface Warehouse {
  id: string;
  name: string;
  code: string;
  branchId: string | null;
  isDefault: boolean;
  isActive: boolean;
  isSellable: boolean;
  city: string | null;
  branch?: { id: string; name: string; code: string } | null;
  _count?: { locations: number; stock: number };
}

export interface Member {
  id: string;
  status: 'INVITED' | 'ACTIVE' | 'SUSPENDED' | 'REMOVED';
  isOwner: boolean;
  jobTitle: string | null;
  employeeCode: string | null;
  allBranches: boolean;
  allWarehouses: boolean;
  joinedAt: string | null;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    phone: string | null;
    lastLoginAt: string | null;
  };
  department: { id: string; name: string } | null;
  roles: { id: string; name: string; isOwnerRole: boolean }[];
  branchIds: string[];
  warehouseIds: string[];
}

export interface Role {
  id: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  isOwnerRole: boolean;
  memberCount: number;
  permissions: PermissionCode[];
}

export interface PermissionGroupView {
  group: string;
  permissions: { code: PermissionCode; group: string; description: string }[];
}

export interface Category {
  id: string;
  name: string;
  code: string | null;
  parentId: string | null;
  sortOrder: number;
  isActive: boolean;
  parent?: { id: string; name: string } | null;
  children?: { id: string; name: string; sortOrder: number }[];
  _count?: { children: number; products: number };
}

/** A category with its descendants nested — `GET /categories/tree`. */
export interface CategoryNode {
  id: string;
  name: string;
  code: string | null;
  parentId: string | null;
  sortOrder: number;
  isActive: boolean;
  children: CategoryNode[];
}

export interface Brand {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  _count?: { products: number };
}

export interface Unit {
  id: string;
  name: string;
  symbol: string;
  dimension: 'COUNT' | 'WEIGHT' | 'VOLUME' | 'LENGTH' | 'AREA' | 'TIME';
  isBaseUnit: boolean;
  precision: number;
  isActive: boolean;
}

export interface UnitConversion {
  id: string;
  fromUnitId: string;
  toUnitId: string;
  factor: string;
  productId: string | null;
  fromUnit: { id: string; name: string; symbol: string };
  toUnit: { id: string; name: string; symbol: string };
  product: { id: string; name: string; sku: string } | null;
}

export interface ProductVariant {
  id: string;
  name: string;
  sku: string;
  barcode: string | null;
  isDefault: boolean;
  isActive: boolean;
  costPrice: string | null;
  sellingPrice: string | null;
  reorderLevel: string | null;
  /**
   * In base units, in the warehouse asked about — only when the list was asked
   * for stock. null for a product that tracks none.
   */
  available?: string | null;
}

/** A customer holding money with the business: deposits and return credits not yet spent. */
export interface CustomerDeposit {
  id: string;
  code: string;
  name: string;
  phone: string | null;
  credit: string;
  lastDepositAt: string | null;
}

/** A collect-later sale with goods still owed to the customer. */
export interface UncollectedSale {
  id: string;
  number: string;
  issuedAt: string;
  customer: { id: string; code: string; name: string; phone: string | null } | null;
  total: string;
  paymentStatus: SalePaymentStatus;
  dueAmount: string;
  lastCollectedAt: string | null;
  items: { id: string; description: string; quantity: string; collected: string; waiting: string }[];
}

/** A receipt that was deleted, read as the return it amounts to. */
export interface DeletedSale {
  id: string;
  number: string;
  issuedAt: string;
  deletedAt: string;
  deletedBy: string | null;
  reason: string | null;
  customer: { id: string; code: string; name: string } | null;
  total: string;
  restocked: { description: string; quantity: string }[];
  refunded: string;
  refunds: { payment: string; amount: string; method: string | null; account: string | null }[];
}

export interface ProductUnitRow {
  id: string;
  unitId: string;
  factor: string;
  barcode: string | null;
  sellingPrice: string | null;
  isPurchaseDefault: boolean;
  isSalesDefault: boolean;
  unit: { id: string; name: string; symbol: string };
}

export interface Product {
  id: string;
  name: string;
  sku: string;
  barcode: string | null;
  type: 'STOCKED' | 'SERVICE' | 'NON_STOCKED' | 'BUNDLE' | 'RAW_MATERIAL' | 'FINISHED_GOOD';
  status: 'ACTIVE' | 'INACTIVE' | 'DISCONTINUED';
  description: string | null;
  categoryId: string | null;
  brandId: string | null;
  baseUnitId: string;
  costPrice: string;
  sellingPrice: string;
  wholesalePrice: string | null;
  reorderLevel: string | null;
  minStock: string | null;
  maxStock: string | null;
  trackInventory: boolean;
  trackBatches: boolean;
  trackExpiry: boolean;
  trackSerials: boolean;
  hasVariants: boolean;
  isSellable: boolean;
  isPurchasable: boolean;
  category?: { id: string; name: string } | null;
  brand?: { id: string; name: string } | null;
  baseUnit?: { id: string; name: string; symbol: string; precision?: number };
  variants?: ProductVariant[];
  productUnits?: ProductUnitRow[];
  _count?: { variants: number };
}

export type TransferStatus =
  | 'DRAFT' | 'REQUESTED' | 'APPROVED' | 'DISPATCHED' | 'RECEIVED' | 'CANCELLED' | 'REJECTED';

export interface TransferItem {
  id: string;
  variantId: string;
  batchId: string | null;
  requestedQty: string;
  dispatchedQty: string;
  receivedQty: string;
  varianceQty: string;
  unitCost: string;
  notes: string | null;
  variant: {
    id: string;
    name: string;
    sku: string;
    product: { id: string; name: string; sku: string; baseUnitId: string };
  };
  batch: { id: string; batchNumber: string } | null;
}

export interface Transfer {
  id: string;
  number: string;
  status: TransferStatus;
  sourceWarehouseId: string;
  destinationWarehouseId: string;
  expectedDate: string | null;
  notes: string | null;
  rejectReason: string | null;
  totalCost: string;
  requestedAt: string | null;
  approvedAt: string | null;
  dispatchedAt: string | null;
  receivedAt: string | null;
  createdAt: string;
  sourceWarehouse: { id: string; name: string; code: string };
  destinationWarehouse: { id: string; name: string; code: string };
  items?: TransferItem[];
  _count?: { items: number };
}

export type StockCountStatus =
  | 'DRAFT' | 'IN_PROGRESS' | 'PENDING_APPROVAL' | 'APPROVED' | 'CANCELLED';

export interface StockCountItemRow {
  id: string;
  variantId: string;
  expectedQty: string;
  countedQty: string | null;
  varianceQty: string;
  unitCost: string;
  varianceValue: string;
  notes: string | null;
  variant: {
    id: string;
    name: string;
    sku: string;
    product: { id: string; name: string; sku: string };
  };
  batch: { id: string; batchNumber: string } | null;
}

export interface StockCount {
  id: string;
  number: string;
  status: StockCountStatus;
  scope: string;
  warehouseId: string;
  notes: string | null;
  totalVarianceValue: string;
  startedAt: string | null;
  completedAt: string | null;
  approvedAt: string | null;
  createdAt: string;
  warehouse: { id: string; name: string; code: string };
  progress?: { counted: number; total: number };
  _count?: { items: number };
}

export interface Customer {
  id: string;
  code: string;
  name: string;
  customerType: 'INDIVIDUAL' | 'COMPANY';
  groupId: string | null;
  email: string | null;
  phone: string | null;
  city: string | null;
  taxNumber: string | null;
  notes: string | null;
  isActive: boolean;
  balance: string;
  creditLimit: string;
  openingBalance: string;
  loyaltyPoints: string;
  paymentTermDays: number;
  /** null when no credit limit is set. */
  availableCredit: string | null;
  isOverLimit: boolean;
  group?: { id: string; name: string; discountPercent: string | null } | null;
  _count?: { sales: number; invoices: number; payments: number };
}

export interface CustomerGroup {
  id: string;
  name: string;
  description: string | null;
  discountPercent: string | null;
  _count?: { customers: number };
}

export interface StatementEntry {
  id: string;
  entryType: 'OPENING' | 'INVOICE' | 'PAYMENT' | 'CREDIT_NOTE' | 'REFUND' | 'ADJUSTMENT';
  referenceType: string | null;
  referenceNumber: string | null;
  debit: string;
  credit: string;
  balanceAfter: string;
  description: string | null;
  occurredAt: string;
}

export type SaleType = 'QUOTATION' | 'ORDER' | 'INVOICE' | 'POS_SALE';
export type SaleStatus =
  | 'DRAFT' | 'PENDING' | 'CONFIRMED' | 'PARTIALLY_DELIVERED'
  | 'DELIVERED' | 'COMPLETED' | 'CANCELLED' | 'EXPIRED';
export type SalePaymentStatus =
  | 'UNPAID' | 'PARTIALLY_PAID' | 'PAID' | 'OVERPAID' | 'REFUNDED';

export interface SaleItem {
  id: string;
  variantId: string;
  description: string;
  quantity: string;
  unitId: string;
  unitPrice: string;
  unitCost: string;
  discountType: 'PERCENTAGE' | 'FIXED' | null;
  discountValue: string;
  discountAmount: string;
  taxRate: string;
  taxAmount: string;
  lineTotal: string;
  returnedQty: string;
  /** On a collect-later sale: how much has been handed over. */
  deliveredQty?: string;
  notes: string | null;
  variant: {
    id: string;
    sku: string;
    name: string;
    product: { id: string; name: string; sku: string };
  };
}

export interface Sale {
  id: string;
  number: string;
  type: SaleType;
  status: SaleStatus;
  paymentStatus: SalePaymentStatus;
  warehouseId: string;
  customerId: string | null;
  subtotal: string;
  discountType: 'PERCENTAGE' | 'FIXED' | null;
  discountValue: string;
  discountAmount: string;
  taxAmount: string;
  shippingAmount: string;
  total: string;
  paidAmount: string;
  dueAmount: string;
  refundedAmount: string;
  costOfGoods: string;
  notes: string | null;
  issuedAt: string;
  dueDate: string | null;
  createdAt: string;
  /** Set when the sale has been hidden from the list. Never destroyed. */
  deletedAt?: string | null;
  deleteReason?: string | null;
  /** Paid for, but the customer will come back for the goods. */
  collectLater?: boolean;
  customer?: { id: string; code: string; name: string; balance?: string } | null;
  branch?: { id: string; name: string } | null;
  items?: SaleItem[];
  payments?: {
    id: string;
    amount: string;
    payment: { id: string; number: string; receivedAt: string; method: { name: string } };
  }[];
  _count?: { items: number };
}

export interface FinancialAccount {
  id: string;
  name: string;
  type: 'CASH' | 'BANK' | 'MOBILE_MONEY' | 'OTHER';
  accountNumber: string | null;
  bankName: string | null;
  currency: string;
  balance: string;
  openingBalance: string;
  isActive: boolean;
  paymentMethods?: { id: string; name: string; type: string; isActive: boolean }[];
  /** What stands in the way of deleting it. */
  _count?: {
    paymentMethods: number;
    transactions?: number;
    payments?: number;
    expenses?: number;
    purchaseOrders?: number;
  };
}

export interface AccountTransaction {
  id: string;
  type: string;
  direction: 'IN' | 'OUT';
  amount: string;
  balanceBefore: string;
  balanceAfter: string;
  referenceType: string;
  referenceId?: string | null;
  referenceNumber: string | null;
  notes: string | null;
  occurredAt: string;
  createdAt?: string;
  createdBy?: { id: string; firstName: string; lastName: string; email: string } | null;
  account: { id: string; name: string; type: string };
}

export interface DailyBalance {
  date: string;
  accounts: {
    accountId: string;
    name: string;
    type: string;
    opening: string;
    moneyIn: string;
    moneyOut: string;
    closing: string;
    balance: string;
  }[];
  totals: { opening: string; moneyIn: string; moneyOut: string; closing: string };
}

export interface PaymentMethod {
  id: string;
  name: string;
  type: 'CASH' | 'BANK_TRANSFER' | 'CARD' | 'MOBILE_MONEY' | 'CHEQUE' | 'CREDIT' | 'STORE_CREDIT' | 'OTHER';
  feePercent: string;
  requiresReference: boolean;
  isDefault: boolean;
  isActive?: boolean;
  accountId?: string | null;
  account?: { id: string; name: string; type: string; isActive: boolean } | null;
}

export interface Payment {
  id: string;
  number: string;
  amount: string;
  unallocated: string;
  feeAmount: string;
  reference: string | null;
  status: string;
  notes: string | null;
  receivedAt: string;
  voidedAt: string | null;
  voidReason: string | null;
  method: { id: string; name: string; type: string };
  customer: { id: string; code: string; name: string } | null;
  allocations?: {
    id: string;
    amount: string;
    sale?: { id: string; number: string; total: string } | null;
    saleId?: string | null;
  }[];
}

export type ReturnStatus =
  | 'DRAFT' | 'PENDING_INSPECTION' | 'APPROVED' | 'REJECTED' | 'COMPLETED' | 'CANCELLED';
export type ReturnCondition = 'RESELLABLE' | 'DAMAGED' | 'EXPIRED' | 'MISSING';

export interface SalesReturnItem {
  id: string;
  saleItemId: string | null;
  variantId: string;
  quantity: string;
  unitPrice: string;
  lineTotal: string;
  condition: ReturnCondition;
  restock: boolean;
  notes: string | null;
  variant: {
    id: string;
    sku: string;
    name: string;
    product: { id: string; name: string; sku: string };
  };
}

export interface SalesReturn {
  id: string;
  number: string;
  status: ReturnStatus;
  resolution: 'REFUND' | 'STORE_CREDIT' | 'REPLACEMENT' | 'SUPPLIER_CREDIT';
  subtotal: string;
  total: string;
  refundedAmount: string;
  restockingFee: string;
  reason: string | null;
  notes: string | null;
  returnedAt: string;
  customer: { id: string; code: string; name: string } | null;
  sale: { id: string; number: string; total?: string } | null;
  items?: SalesReturnItem[];
  _count?: { items: number };
}

export interface Invoice {
  id: string;
  number: string;
  status: string;
  paymentStatus: SalePaymentStatus;
  subtotal: string;
  taxAmount: string;
  discountAmount: string;
  total: string;
  paidAmount: string;
  dueAmount: string;
  issuedAt: string;
  dueDate: string | null;
  terms: string | null;
  notes: string | null;
  voidReason: string | null;
  isOverdue?: boolean;
  customer: { id: string; code: string; name: string; email?: string | null } | null;
  sale: { id: string; number: string } | null;
  items?: {
    id: string;
    description: string;
    quantity: string;
    unitPrice: string;
    discountAmount: string;
    taxAmount: string;
    lineTotal: string;
  }[];
}

export interface PosSession {
  id: string;
  registerName: string;
  status: 'OPEN' | 'CLOSED' | 'RECONCILED';
  openingFloat: string;
  expectedCash: string;
  countedCash: string | null;
  cashVariance: string;
  totalSales: string;
  totalRefunds: string;
  saleCount: number;
  openedAt: string;
  closedAt: string | null;
  notes: string | null;
  branch?: { id: string; name: string };
  heldCarts?: { id: string; reference: string; total: string; itemCount: number }[];
}

export interface HeldCart {
  id: string;
  reference: string;
  itemCount: number;
  total: string;
  snapshot: Record<string, unknown>;
}

export interface PosCheckoutResult {
  sale: Sale;
  changeDue: string;
  sessionId: string;
}

export interface Supplier {
  id: string;
  code: string;
  name: string;
  contactPerson: string | null;
  email: string | null;
  phone: string | null;
  city: string | null;
  isActive: boolean;
  balance: string;
  openingBalance: string;
  creditLimit: string;
  paymentTermDays: number;
  notes: string | null;
  _count?: { purchaseOrders: number; goodsReceipts: number; invoices?: number };
}

export interface SupplierStatementEntry {
  id: string;
  entryType: string;
  referenceNumber: string | null;
  debit: string;
  credit: string;
  balanceAfter: string;
  description: string | null;
  occurredAt: string;
}

export type PurchaseOrderStatus =
  | 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'ORDERED'
  | 'PARTIALLY_RECEIVED' | 'FULLY_RECEIVED' | 'CANCELLED';

export interface PurchaseOrderItem {
  id: string;
  variantId: string;
  description: string;
  quantity: string;
  receivedQty: string;
  outstandingQty: string;
  unitCost: string;
  taxAmount: string;
  lineTotal: string;
}

export interface PurchaseOrder {
  id: string;
  number: string;
  status: PurchaseOrderStatus;
  supplierId: string;
  warehouseId: string;
  subtotal: string;
  taxAmount: string;
  shippingAmount: string;
  otherCharges: string;
  total: string;
  expectedDate: string | null;
  notes: string | null;
  createdAt: string;
  supplier: { id: string; code: string; name: string; balance?: string };
  items?: PurchaseOrderItem[];
  receipts?: { id: string; number: string; status: string; receivedAt: string; totalCost: string }[];
  _count?: { items: number; receipts: number };
  payingAccountId?: string | null;
  payingAccount?: { id: string; name: string; type: string } | null;
}

export interface GoodsReceipt {
  id: string;
  number: string;
  status: 'DRAFT' | 'POSTED' | 'CANCELLED';
  supplierDeliveryNote: string | null;
  receivedAt: string;
  postedAt: string | null;
  totalCost: string;
  notes: string | null;
  supplier: { id: string; code: string; name: string };
  warehouse: { id: string; name: string };
  order: { id: string; number: string } | null;
  items?: {
    id: string;
    variantId: string;
    quantity: string;
    unitCost: string;
    rejectedQty: string;
    lineTotal: string;
    variant: { id: string; sku: string; name: string; product: { name: string } };
  }[];
  _count?: { items: number };
}

export interface SupplierInvoice {
  id: string;
  number: string;
  supplierRef: string | null;
  status: string;
  paymentStatus: SalePaymentStatus;
  subtotal: string;
  taxAmount: string;
  total: string;
  paidAmount: string;
  dueAmount: string;
  invoiceDate: string;
  dueDate: string | null;
  isOverdue?: boolean;
  supplier: { id: string; code: string; name: string };
  order: { id: string; number: string } | null;
}

export interface SupplierReturn {
  id: string;
  number: string;
  status: ReturnStatus;
  subtotal: string;
  total: string;
  reason: string | null;
  returnedAt: string;
  supplier: { id: string; code: string; name: string };
  items?: {
    id: string;
    variantId: string;
    quantity: string;
    unitCost: string;
    lineTotal: string;
    variant: { id: string; sku: string; name: string; product: { name: string } };
  }[];
  _count?: { items: number };
}

export type ExpenseStatus = 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED' | 'PAID';

export interface Expense {
  id: string;
  number: string;
  status: ExpenseStatus;
  description: string;
  amount: string;
  taxAmount: string;
  total: string;
  expenseDate: string;
  reference: string | null;
  notes: string | null;
  rejectReason: string | null;
  category: { id: string; name: string };
  supplier: { id: string; name: string } | null;
  /** Which account paid it. Set when it is paid, if not before. */
  accountId?: string | null;
  account?: { id: string; name: string } | null;
  isRecurring?: boolean;
  recurrenceRule?: string | null;
  nextOccurrence?: string | null;
  /** Set when the expense has been deleted and its money given back. */
  deletedAt?: string | null;
  deleteReason?: string | null;
}

export interface ExpenseCategory {
  id: string;
  name: string;
  description: string | null;
  parent: { id: string; name: string } | null;
  _count?: { expenses: number };
}

export interface StockRow {
  id: string;
  warehouse: { id: string; name: string; code: string };
  batch: { id: string; batchNumber: string; expiresAt: string | null } | null;
  location: { id: string; code: string } | null;
  variant: { id: string; name: string; sku: string; barcode: string | null };
  product: {
    id: string;
    name: string;
    sku: string;
    baseUnit: { id: string; name: string; symbol: string };
    category: { id: string; name: string } | null;
  };
  quantity: string;
  reservedQuantity: string;
  availableQuantity: string;
  averageCost: string;
  stockValue: string;
  reorderLevel: string | null;
  isLowStock: boolean;
  isOutOfStock: boolean;
  lastMovementAt: string | null;
}

export interface LedgerEntry {
  id: string;
  type: string;
  direction: 'IN' | 'OUT';
  quantity: string;
  enteredQty: string;
  unitCost: string;
  totalCost: string;
  balanceBefore: string;
  balanceAfter: string;
  referenceType: string;
  referenceNumber: string | null;
  notes: string | null;
  occurredAt: string;
  warehouse: { id: string; name: string; code: string };
  unit: { id: string; symbol: string };
  batch: { id: string; batchNumber: string } | null;
  variant: { id: string; name: string; sku: string; product: { id: string; name: string; sku: string } };
}

export interface SalesReport {
  range: { from: string; to: string };
  summary: {
    saleCount: number;
    gross: string;
    discounts: string;
    tax: string;
    revenue: string;
    collected: string;
    outstanding: string;
    refunded: string;
    averageSale: string;
    costOfGoods?: string;
    grossProfit?: string;
    marginPercent?: string | null;
    expenses?: string;
    expensesPaid?: string;
    expensesUnpaid?: string;
    netProfit?: string;
    netMarginPercent?: string | null;
  };
  byPaymentStatus: {
    paymentStatus: string;
    saleCount: number;
    total: string;
    outstanding: string;
  }[];
  series: { date: string; saleCount: number; revenue: string; profit?: string }[];
  topProducts: {
    variantId: string; sku: string; name: string;
    quantity: string; revenue: string; profit?: string;
  }[];
  topCustomers: {
    customerId: string | null; name: string; code: string | null;
    saleCount: number; revenue: string; outstanding: string;
  }[];
  expensesByCategory: {
    categoryId: string; name: string; expenseCount: number; amount: string;
  }[];
  /** Whether this reader is allowed cost and profit at all. */
  includesCost: boolean;
}

export interface Property {
  id: string;
  reference: string;
  name: string;
  kind: 'LAND' | 'PLOT' | 'HOUSE' | 'COMMERCIAL' | 'OTHER';
  status: 'AVAILABLE' | 'RESERVED' | 'SOLD' | 'WITHDRAWN';
  titleNumber: string | null;
  plotNumber: string | null;
  blockNumber: string | null;
  location: string | null;
  district: string | null;
  coordinates: string | null;
  area: string | null;
  areaUnit: string;
  askingPrice: string;
  acquisitionCost: string | null;
  description: string | null;
  notes: string | null;
  sales?: {
    id: string; number: string; status: string; price: string;
    paidAmount: string; dueAmount: string; agreedAt: string;
    customer: { id: string; name: string; code: string };
  }[];
}

export interface PropertySale {
  id: string;
  number: string;
  status: 'AGREED' | 'COMPLETED' | 'CANCELLED';
  paymentPlan: 'OUTRIGHT' | 'INSTALMENTS';
  price: string;
  paidAmount: string;
  dueAmount: string;
  agreedAt: string;
  completedAt: string | null;
  /** When the next instalment is expected. Null on an outright sale. */
  nextDueAt: string | null;
  remindedAt: string | null;
  /** Set when the sale has been hidden and its money taken back out. */
  deletedAt?: string | null;
  deleteReason?: string | null;
  cancelReason: string | null;
  notes: string | null;
  property: Property | { id: string; reference: string; name: string; location: string | null };
  customer: { id: string; name: string; code: string; phone?: string | null };
  account?: { id: string; name: string } | null;
  payments?: {
    id: string; amount: string; reference: string | null; notes: string | null;
    receivedAt: string;
    method: { id: string; name: string } | null;
    account: { id: string; name: string };
  }[];
  _count?: { payments: number };
}


export interface ProfitAndLoss {
  range: { from: string; to: string };
  basis: string;
  revenue: {
    goods: string;
    goodsSaleCount: number;
    property: string;
    propertySaleCount: number;
    total: string;
    taxCharged: string;
    discountsGiven: string;
    returns: string;
  };
  costOfSales: { goods: string; property: string; total: string };
  grossProfit: string;
  grossMarginPercent: string | null;
  operatingExpenses: {
    total: string;
    count: number;
    byCategory: { categoryId: string; name: string; count: number; amount: string }[];
  };
  netProfit: string;
  netMarginPercent: string | null;
}

export interface CashFlowReport {
  range: { from: string; to: string };
  basis: string;
  summary: {
    opening: string;
    received: string;
    paid: string;
    movement: string;
    closing: string;
    fromTrading: string;
  };
  byType: { type: string; movements: number; received: string; paid: string; net: string }[];
  byAccount: {
    accountId: string;
    name: string;
    type: string;
    isActive: boolean;
    opening: string;
    received: string;
    paid: string;
    closing: string;
    expected: string;
  }[];
}

export interface PositionReport {
  asAt: string;
  basis: string;
  held: {
    cash: string;
    stockAtCost: string;
    owedByCustomers: string;
    customerCount: number;
    propertyAtCost: string;
    propertyCount: number;
    propertyAtAskingPrice: string;
    total: string;
  };
  owed: {
    toSuppliers: string;
    supplierInvoiceCount: number;
    approvedExpenses: string;
    expenseCount: number;
    total: string;
  };
  netWorth: string;
}

/** A list that also carries totals for the whole filter, not the page. */
export interface SummarisedList<T, S> extends ListResult<T> {
  summary: S;
}

export interface MoneySummary {
  count: number;
  [figure: string]: string | number;
}

export interface PropertySalesResult extends ListResult<PropertySale> {
  summary: { saleCount: number; agreed: string; collected: string; outstanding: string };
}

export interface PropertySummary {
  available: { count: number; value: string };
  reserved: { count: number; value: string };
  sold: { count: number; value: string };
  sales: { count: number; agreed: string; collected: string; outstanding: string };
}

export interface SignedInDevice {
  id: string;
  deviceName: string | null;
  ipAddress: string | null;
  lastSeenAt: string;
  isCurrent: boolean;
}

export interface StockValuation {
  totalValue: string;
  byWarehouse: {
    warehouseId: string;
    warehouseName: string;
    skuCount: number;
    totalQuantity: string;
    totalValue: string;
  }[];
}

export interface AuditEntry {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  entityLabel: string | null;
  changedFields: string[];
  ipAddress: string | null;
  occurredAt: string;
  user: { id: string; firstName: string; lastName: string; email: string } | null;
}

export interface NotificationItem {
  id: string;
  eventType: string;
  title: string;
  body: string;
  actionUrl: string | null;
  readAt: string | null;
  createdAt: string;
}

export interface SubscriptionView {
  status: string;
  billingCycle: string;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  plan: { code: string; name: string; monthlyPrice: string; currency: string };
  features: string[];
  usage: Record<string, { used: number; max: number | null }>;
}

export interface BusinessTypeTemplateView {
  code: string;
  name: string;
  description: string;
  suggestedFeatures: string[];
}

type ListQuery = Record<string, string | number | boolean | undefined>;

const listParams = (query: ListQuery = {}): ListQuery =>
  Object.fromEntries(
    Object.entries(query).filter(([, value]) => value !== undefined && value !== ''),
  );

/**
 * Pickers load a page at a time and search on the server, so a catalogue of
 * any size can be reached — a fixed `limit: 200` silently hid everything past
 * the 200th row, search included.
 */
const PICKER_PAGE_SIZE = 50;

const pagedListOptions = {
  initialPageParam: 1,
  getNextPageParam: (last: ListResult<unknown>) => (last.meta.hasNext ? last.meta.page + 1 : undefined),
};

const pickerParams = (queryArg: ListQuery, page: number): ListQuery =>
  listParams({ limit: PICKER_PAGE_SIZE, ...queryArg, page });

// ------------------------------------------------------------------- endpoints

export const api = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // --- auth ---
    login: builder.mutation<LoginResult, { email: string; password: string; deviceName?: string }>({
      query: (body) => ({ url: '/auth/login', method: 'POST', body }),
      transformResponse: unwrap<LoginResult>,
    }),
    register: builder.mutation<
      LoginResult,
      { email: string; password: string; firstName: string; lastName: string; invitationToken?: string }
    >({
      query: (body) => ({ url: '/auth/register', method: 'POST', body }),
      transformResponse: unwrap<LoginResult>,
    }),
    logout: builder.mutation<void, void>({
      query: () => ({ url: '/auth/logout', method: 'POST' }),
    }),
    forgotPassword: builder.mutation<{ message: string }, { email: string }>({
      query: (body) => ({ url: '/auth/forgot-password', method: 'POST', body }),
      transformResponse: unwrap<{ message: string }>,
    }),
    resetPassword: builder.mutation<{ message: string }, { token: string; password: string }>({
      query: (body) => ({ url: '/auth/reset-password', method: 'POST', body }),
      transformResponse: unwrap<{ message: string }>,
    }),
    changePassword: builder.mutation<
      { message: string },
      { currentPassword: string; newPassword: string; revokeOtherSessions?: boolean }
    >({
      query: (body) => ({ url: '/auth/change-password', method: 'POST', body }),
      transformResponse: unwrap<{ message: string }>,
    }),
    verifyEmail: builder.mutation<{ email: string }, { token: string }>({
      query: (body) => ({ url: '/auth/verify-email', method: 'POST', body }),
      transformResponse: unwrap<{ email: string }>,
    }),
    me: builder.query<{ user: AuthenticatedUser; organizations: OrganizationSummary[] }, void>({
      query: () => '/auth/me',
      transformResponse: unwrap<{ user: AuthenticatedUser; organizations: OrganizationSummary[] }>,
      providesTags: ['Auth'],
    }),
    updateProfile: builder.mutation<AuthenticatedUser, Partial<{ firstName: string; lastName: string; phone: string; timezone: string; locale: string }>>({
      query: (body) => ({ url: '/auth/me', method: 'PATCH', body }),
      transformResponse: unwrap<AuthenticatedUser>,
      invalidatesTags: ['Auth'],
    }),
    sessions: builder.query<ListResult<SignedInDevice>, ListQuery | void>({
      query: (params) => ({ url: '/auth/sessions', params: listParams(params ?? {}) }),
      transformResponse: unwrapList<SignedInDevice>,
      providesTags: ['Session'],
    }),
    revokeSession: builder.mutation<void, string>({
      query: (id) => ({ url: `/auth/sessions/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Session'],
    }),

    // --- organizations ---
    businessTypes: builder.query<BusinessTypeTemplateView[], void>({
      query: () => '/organizations/business-types',
      transformResponse: unwrap<BusinessTypeTemplateView[]>,
    }),
    createOrganization: builder.mutation<
      { id: string; name: string; slug: string },
      {
        name: string;
        businessType: string;
        currency: string;
        countryCode: string;
        timezone?: string;
        branchName?: string;
        warehouseName?: string;
      }
    >({
      query: (body) => ({ url: '/organizations', method: 'POST', body }),
      transformResponse: unwrap<{ id: string; name: string; slug: string }>,
      invalidatesTags: ['Auth', 'Organization'],
    }),
    organizationContext: builder.query<OrganizationContext, void>({
      query: () => '/organizations/me/context',
      transformResponse: unwrap<OrganizationContext>,
      providesTags: ['OrganizationContext'],
    }),
    organization: builder.query<Record<string, unknown>, void>({
      query: () => '/organizations/me',
      transformResponse: unwrap<Record<string, unknown>>,
      providesTags: ['Organization'],
    }),
    updateOrganization: builder.mutation<Record<string, unknown>, Record<string, unknown>>({
      query: (body) => ({ url: '/organizations/me', method: 'PATCH', body }),
      transformResponse: unwrap<Record<string, unknown>>,
      invalidatesTags: ['Organization', 'OrganizationContext'],
    }),
    settings: builder.query<BusinessSettingsPayload & { id: string }, void>({
      query: () => '/organizations/me/settings',
      transformResponse: unwrap<BusinessSettingsPayload & { id: string }>,
      providesTags: ['Settings'],
    }),
    updateSettings: builder.mutation<BusinessSettingsPayload, Partial<BusinessSettingsPayload>>({
      query: (body) => ({ url: '/organizations/me/settings', method: 'PATCH', body }),
      transformResponse: unwrap<BusinessSettingsPayload>,
      invalidatesTags: ['Settings', 'OrganizationContext'],
    }),
    features: builder.query<{ enabled: string[]; overrides: { featureKey: string; enabled: boolean }[] }, void>({
      query: () => '/organizations/me/features',
      transformResponse: unwrap<{ enabled: string[]; overrides: { featureKey: string; enabled: boolean }[] }>,
      providesTags: ['Feature'],
    }),
    toggleFeature: builder.mutation<unknown, { featureKey: string; enabled: boolean }>({
      query: (body) => ({ url: '/organizations/me/features', method: 'PATCH', body }),
      invalidatesTags: ['Feature', 'OrganizationContext'],
    }),
    completeOnboarding: builder.mutation<unknown, { step: string }>({
      query: (body) => ({ url: '/organizations/me/onboarding', method: 'PATCH', body }),
      invalidatesTags: ['Organization'],
    }),

    // --- subscription ---
    subscription: builder.query<SubscriptionView, void>({
      query: () => '/subscriptions/me',
      transformResponse: unwrap<SubscriptionView>,
      providesTags: ['Subscription'],
    }),
    plans: builder.query<
      { code: string; name: string; monthlyPrice: string; yearlyPrice: string; currency: string }[],
      void
    >({
      query: () => '/subscriptions/plans',
      transformResponse: unwrap<
        { code: string; name: string; monthlyPrice: string; yearlyPrice: string; currency: string }[]
      >,
      providesTags: ['Subscription'],
    }),
    renewSubscription: builder.mutation<
      SubscriptionView,
      { paidUntil: string; planCode?: string; billingCycle?: string }
    >({
      query: (body) => ({ url: '/subscriptions/me/renew', method: 'POST', body }),
      transformResponse: unwrap<SubscriptionView>,
      invalidatesTags: ['Subscription', 'OrganizationContext'],
    }),

    // --- branches ---
    branches: builder.query<ListResult<Branch>, ListQuery | void>({
      query: (params) => ({ url: '/branches', params: listParams(params ?? {}) }),
      transformResponse: unwrapList<Branch>,
      providesTags: ['Branch'],
    }),
    createBranch: builder.mutation<Branch, Partial<Branch> & { name: string; code: string }>({
      query: (body) => ({ url: '/branches', method: 'POST', body }),
      transformResponse: unwrap<Branch>,
      invalidatesTags: ['Branch', 'OrganizationContext'],
    }),
    updateBranch: builder.mutation<Branch, { id: string; body: Partial<Branch> }>({
      query: ({ id, body }) => ({ url: `/branches/${id}`, method: 'PATCH', body }),
      transformResponse: unwrap<Branch>,
      invalidatesTags: ['Branch', 'OrganizationContext'],
    }),
    deleteBranch: builder.mutation<void, string>({
      query: (id) => ({ url: `/branches/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Branch', 'OrganizationContext'],
    }),

    // --- warehouses ---
    warehouses: builder.query<ListResult<Warehouse>, ListQuery | void>({
      query: (params) => ({ url: '/warehouses', params: listParams(params ?? {}) }),
      transformResponse: unwrapList<Warehouse>,
      providesTags: ['Warehouse'],
    }),
    createWarehouse: builder.mutation<Warehouse, Partial<Warehouse> & { name: string; code: string }>({
      query: (body) => ({ url: '/warehouses', method: 'POST', body }),
      transformResponse: unwrap<Warehouse>,
      invalidatesTags: ['Warehouse', 'OrganizationContext'],
    }),
    updateWarehouse: builder.mutation<Warehouse, { id: string; body: Partial<Warehouse> }>({
      query: ({ id, body }) => ({ url: `/warehouses/${id}`, method: 'PATCH', body }),
      transformResponse: unwrap<Warehouse>,
      invalidatesTags: ['Warehouse', 'OrganizationContext'],
    }),
    deleteWarehouse: builder.mutation<void, string>({
      query: (id) => ({ url: `/warehouses/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Warehouse', 'OrganizationContext'],
    }),

    // --- members & roles ---
    members: builder.query<SummarisedList<Member, MoneySummary>, ListQuery | void>({
      query: (params) => ({ url: '/members', params: listParams(params ?? {}) }),
      transformResponse: (response: {
        data: Member[]; meta: ListResult<Member>['meta']; summary: MoneySummary;
      }) => ({ items: response.data, meta: response.meta, summary: response.summary }),
      providesTags: ['Member'],
    }),
    setMemberPassword: builder.mutation<
      { id: string; sessionsRevoked: number },
      { id: string; password: string }
    >({
      query: ({ id, password }) => ({
        url: `/members/${id}/password`, method: 'POST', body: { password },
      }),
      transformResponse: unwrap<{ id: string; sessionsRevoked: number }>,
      invalidatesTags: ['Member'],
    }),
    createMember: builder.mutation<Member, Record<string, unknown>>({
      query: (body) => ({ url: '/members', method: 'POST', body }),
      transformResponse: unwrap<Member>,
      invalidatesTags: ['Member'],
    }),
    inviteMember: builder.mutation<
      Member,
      {
        email: string;
        firstName: string;
        lastName: string;
        roleIds: string[];
        jobTitle?: string;
        allBranches?: boolean;
        branchIds?: string[];
      }
    >({
      query: (body) => ({ url: '/members/invite', method: 'POST', body }),
      transformResponse: unwrap<Member>,
      invalidatesTags: ['Member'],
    }),
    updateMember: builder.mutation<Member, { id: string; body: Record<string, unknown> }>({
      query: ({ id, body }) => ({ url: `/members/${id}`, method: 'PATCH', body }),
      transformResponse: unwrap<Member>,
      invalidatesTags: ['Member'],
    }),
    removeMember: builder.mutation<void, string>({
      query: (id) => ({ url: `/members/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Member'],
    }),
    roles: builder.query<ListResult<Role>, ListQuery | void>({
      query: (params) => ({ url: '/roles', params: listParams(params ?? {}) }),
      transformResponse: unwrapList<Role>,
      providesTags: ['Role'],
    }),
    permissionCatalogue: builder.query<PermissionGroupView[], void>({
      query: () => '/roles/permissions',
      transformResponse: unwrap<PermissionGroupView[]>,
    }),
    createRole: builder.mutation<Role, { name: string; description?: string; permissions: string[] }>({
      query: (body) => ({ url: '/roles', method: 'POST', body }),
      transformResponse: unwrap<Role>,
      invalidatesTags: ['Role'],
    }),
    updateRole: builder.mutation<Role, { id: string; body: { name?: string; description?: string; permissions?: string[] } }>({
      query: ({ id, body }) => ({ url: `/roles/${id}`, method: 'PATCH', body }),
      transformResponse: unwrap<Role>,
      invalidatesTags: ['Role', 'OrganizationContext'],
    }),
    deleteRole: builder.mutation<void, string>({
      query: (id) => ({ url: `/roles/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Role'],
    }),

    // --- inventory ---
    // --- suppliers ---
    suppliers: builder.query<SummarisedList<Supplier, MoneySummary>, ListQuery | void>({
      query: (params) => ({ url: '/suppliers', params: listParams(params ?? {}) }),
      transformResponse: (response: { data: Supplier[]; meta: ListResult<Supplier>['meta']; summary: MoneySummary }) => ({
        items: response.data, meta: response.meta, summary: response.summary,
      }),
      providesTags: ['Supplier'],
    }),
    supplierPages: builder.infiniteQuery<ListResult<Supplier>, ListQuery, number>({
      infiniteQueryOptions: pagedListOptions,
      query: ({ queryArg, pageParam }) => ({ url: '/suppliers', params: pickerParams(queryArg, pageParam) }),
      transformResponse: (response: { data: Supplier[]; meta: ListResult<Supplier>['meta'] }) => ({
        items: response.data, meta: response.meta,
      }),
      providesTags: ['Supplier'],
    }),
    supplier: builder.query<Supplier, string>({
      query: (id) => ({ url: `/suppliers/${id}` }),
      transformResponse: unwrap<Supplier>,
      providesTags: ['Supplier'],
    }),
    createSupplier: builder.mutation<Supplier, Record<string, unknown>>({
      query: (body) => ({ url: '/suppliers', method: 'POST', body }),
      transformResponse: unwrap<Supplier>,
      invalidatesTags: ['Supplier'],
    }),
    updateSupplier: builder.mutation<Supplier, { id: string; body: Record<string, unknown> }>({
      query: ({ id, body }) => ({ url: `/suppliers/${id}`, method: 'PATCH', body }),
      transformResponse: unwrap<Supplier>,
      invalidatesTags: ['Supplier'],
    }),
    deleteSupplier: builder.mutation<void, string>({
      query: (id) => ({ url: `/suppliers/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Supplier'],
    }),
    supplierStatement: builder.query<
      ListResult<SupplierStatementEntry> & { supplier: Partial<Supplier> },
      { id: string; params?: ListQuery }
    >({
      query: ({ id, params }) => ({
        url: `/suppliers/${id}/statement`, params: listParams(params ?? {}),
      }),
      transformResponse: (response: {
        success: true;
        data: SupplierStatementEntry[];
        meta: ListResult<SupplierStatementEntry>['meta'];
        supplier: Partial<Supplier>;
      }) => ({ items: response.data, meta: response.meta, supplier: response.supplier }),
      providesTags: ['Supplier'],
    }),

    // --- purchasing ---
    purchaseOrders: builder.query<SummarisedList<PurchaseOrder, MoneySummary>, ListQuery | void>({
      query: (params) => ({ url: '/purchase-orders', params: listParams(params ?? {}) }),
      transformResponse: (response: { data: PurchaseOrder[]; meta: ListResult<PurchaseOrder>['meta']; summary: MoneySummary }) => ({
        items: response.data, meta: response.meta, summary: response.summary,
      }),
      providesTags: ['PurchaseOrder'],
    }),
    purchaseOrder: builder.query<PurchaseOrder, string>({
      query: (id) => ({ url: `/purchase-orders/${id}` }),
      transformResponse: unwrap<PurchaseOrder>,
      providesTags: ['PurchaseOrder'],
    }),
    createPurchaseOrder: builder.mutation<PurchaseOrder, Record<string, unknown>>({
      query: (body) => ({ url: '/purchase-orders', method: 'POST', body }),
      transformResponse: unwrap<PurchaseOrder>,
      invalidatesTags: ['PurchaseOrder'],
    }),
    /** submit · approve · order · cancel */
    purchaseOrderAction: builder.mutation<
      PurchaseOrder,
      { id: string; action: string; body?: Record<string, unknown> }
    >({
      query: ({ id, action, body }) => ({
        url: `/purchase-orders/${id}/${action}`, method: 'POST', body: body ?? {},
      }),
      transformResponse: unwrap<PurchaseOrder>,
      invalidatesTags: ['PurchaseOrder'],
    }),
    goodsReceipts: builder.query<SummarisedList<GoodsReceipt, MoneySummary>, ListQuery | void>({
      query: (params) => ({ url: '/goods-receipts', params: listParams(params ?? {}) }),
      transformResponse: (response: { data: GoodsReceipt[]; meta: ListResult<GoodsReceipt>['meta']; summary: MoneySummary }) => ({
        items: response.data, meta: response.meta, summary: response.summary,
      }),
      providesTags: ['GoodsReceipt'],
    }),
    goodsReceipt: builder.query<GoodsReceipt, string>({
      query: (id) => ({ url: `/goods-receipts/${id}` }),
      transformResponse: unwrap<GoodsReceipt>,
      providesTags: ['GoodsReceipt'],
    }),
    createGoodsReceipt: builder.mutation<GoodsReceipt, Record<string, unknown>>({
      query: (body) => ({ url: '/goods-receipts', method: 'POST', body }),
      transformResponse: unwrap<GoodsReceipt>,
      invalidatesTags: ['GoodsReceipt', 'PurchaseOrder'],
    }),
    /** post · cancel */
    goodsReceiptAction: builder.mutation<
      GoodsReceipt,
      { id: string; action: string; body?: Record<string, unknown> }
    >({
      query: ({ id, action, body }) => ({
        url: `/goods-receipts/${id}/${action}`, method: 'POST', body: body ?? {},
      }),
      transformResponse: unwrap<GoodsReceipt>,
      // Posting puts goods into stock at the cost paid.
      invalidatesTags: ['GoodsReceipt', 'PurchaseOrder', 'Stock', 'StockLedger', 'Product'],
    }),

    // --- payables ---
    supplierInvoices: builder.query<SummarisedList<SupplierInvoice, MoneySummary>, ListQuery | void>({
      query: (params) => ({ url: '/supplier-invoices', params: listParams(params ?? {}) }),
      transformResponse: (response: { data: SupplierInvoice[]; meta: ListResult<SupplierInvoice>['meta']; summary: MoneySummary }) => ({
        items: response.data, meta: response.meta, summary: response.summary,
      }),
      providesTags: ['SupplierInvoice'],
    }),
    createSupplierInvoice: builder.mutation<SupplierInvoice, Record<string, unknown>>({
      query: (body) => ({ url: '/supplier-invoices', method: 'POST', body }),
      transformResponse: unwrap<SupplierInvoice>,
      invalidatesTags: ['SupplierInvoice', 'Supplier'],
    }),
    voidSupplierInvoice: builder.mutation<SupplierInvoice, { id: string; reason: string }>({
      query: ({ id, reason }) => ({
        url: `/supplier-invoices/${id}/void`, method: 'POST', body: { reason },
      }),
      transformResponse: unwrap<SupplierInvoice>,
      invalidatesTags: ['SupplierInvoice', 'Supplier'],
    }),
    paySupplier: builder.mutation<Record<string, unknown>, Record<string, unknown>>({
      query: (body) => ({ url: '/supplier-payments', method: 'POST', body }),
      transformResponse: unwrap<Record<string, unknown>>,
      invalidatesTags: ['SupplierInvoice', 'Supplier', 'Payment'],
    }),
    supplierReturns: builder.query<ListResult<SupplierReturn>, ListQuery | void>({
      query: (params) => ({ url: '/supplier-returns', params: listParams(params ?? {}) }),
      transformResponse: unwrapList<SupplierReturn>,
      providesTags: ['SupplierReturn'],
    }),
    createSupplierReturn: builder.mutation<SupplierReturn, Record<string, unknown>>({
      query: (body) => ({ url: '/supplier-returns', method: 'POST', body }),
      transformResponse: unwrap<SupplierReturn>,
      invalidatesTags: ['SupplierReturn'],
    }),
    /** approve · cancel */
    supplierReturnAction: builder.mutation<
      SupplierReturn,
      { id: string; action: string; body?: Record<string, unknown> }
    >({
      query: ({ id, action, body }) => ({
        url: `/supplier-returns/${id}/${action}`, method: 'POST', body: body ?? {},
      }),
      transformResponse: unwrap<SupplierReturn>,
      invalidatesTags: ['SupplierReturn', 'Stock', 'StockLedger', 'Supplier'],
    }),

    // --- expenses ---
    expenses: builder.query<SummarisedList<Expense, MoneySummary>, ListQuery | void>({
      query: (params) => ({ url: '/expenses', params: listParams(params ?? {}) }),
      transformResponse: (response: { data: Expense[]; meta: ListResult<Expense>['meta']; summary: MoneySummary }) => ({
        items: response.data, meta: response.meta, summary: response.summary,
      }),
      providesTags: ['Expense'],
    }),
    createExpense: builder.mutation<Expense, Record<string, unknown>>({
      query: (body) => ({ url: '/expenses', method: 'POST', body }),
      transformResponse: unwrap<Expense>,
      invalidatesTags: ['Expense'],
    }),
    updateExpense: builder.mutation<Expense, { id: string; body: Record<string, unknown> }>({
      query: ({ id, body }) => ({ url: `/expenses/${id}`, method: 'PATCH', body }),
      transformResponse: unwrap<Expense>,
      invalidatesTags: ['Expense', 'Account', 'AccountTransaction'],
    }),
    /** submit · approve · reject · pay */
    deleteExpense: builder.mutation<
      { id: string; hidden: boolean; cashEntriesReversed: number },
      { id: string; reason: string }
    >({
      query: ({ id, reason }) => ({ url: `/expenses/${id}`, method: 'DELETE', body: { reason } }),
      transformResponse: unwrap<{ id: string; hidden: boolean; cashEntriesReversed: number }>,
      invalidatesTags: ['Expense', 'Account', 'AccountTransaction'],
    }),
    restoreExpense: builder.mutation<Expense, string>({
      query: (id) => ({ url: `/expenses/${id}/restore`, method: 'POST' }),
      transformResponse: unwrap<Expense>,
      invalidatesTags: ['Expense', 'Account', 'AccountTransaction'],
    }),
    expenseAction: builder.mutation<
      Expense,
      { id: string; action: string; body?: Record<string, unknown> }
    >({
      query: ({ id, action, body }) => ({
        url: `/expenses/${id}/${action}`, method: 'POST', body: body ?? {},
      }),
      transformResponse: unwrap<Expense>,
      invalidatesTags: ['Expense'],
    }),
    expenseCategories: builder.query<ExpenseCategory[], void>({
      query: () => ({ url: '/expenses/categories' }),
      transformResponse: unwrap<ExpenseCategory[]>,
      providesTags: ['ExpenseCategory'],
    }),
    createExpenseCategory: builder.mutation<ExpenseCategory, Record<string, unknown>>({
      query: (body) => ({ url: '/expenses/categories', method: 'POST', body }),
      transformResponse: unwrap<ExpenseCategory>,
      invalidatesTags: ['ExpenseCategory'],
    }),

    // --- customers ---
    customers: builder.query<SummarisedList<Customer, MoneySummary>, ListQuery | void>({
      query: (params) => ({ url: '/customers', params: listParams(params ?? {}) }),
      transformResponse: (response: { data: Customer[]; meta: ListResult<Customer>['meta']; summary: MoneySummary }) => ({
        items: response.data, meta: response.meta, summary: response.summary,
      }),
      providesTags: ['Customer'],
    }),
    customerPages: builder.infiniteQuery<ListResult<Customer>, ListQuery, number>({
      infiniteQueryOptions: pagedListOptions,
      query: ({ queryArg, pageParam }) => ({ url: '/customers', params: pickerParams(queryArg, pageParam) }),
      transformResponse: (response: { data: Customer[]; meta: ListResult<Customer>['meta'] }) => ({
        items: response.data, meta: response.meta,
      }),
      providesTags: ['Customer'],
    }),
    customerDeposits: builder.query<
      SummarisedList<CustomerDeposit, { count: number; held: string }>,
      ListQuery | void
    >({
      query: (params) => ({ url: '/customers/deposits', params: listParams(params ?? {}) }),
      transformResponse: (response: {
        data: CustomerDeposit[];
        meta: ListResult<CustomerDeposit>['meta'];
        summary: { count: number; held: string };
      }) => ({ items: response.data, meta: response.meta, summary: response.summary }),
      providesTags: ['Customer', 'Payment'],
    }),
    customer: builder.query<Customer, string>({
      query: (id) => ({ url: `/customers/${id}` }),
      transformResponse: unwrap<Customer>,
      providesTags: ['Customer'],
    }),
    createCustomer: builder.mutation<Customer, Record<string, unknown>>({
      query: (body) => ({ url: '/customers', method: 'POST', body }),
      transformResponse: unwrap<Customer>,
      invalidatesTags: ['Customer'],
    }),
    updateCustomer: builder.mutation<Customer, { id: string; body: Record<string, unknown> }>({
      query: ({ id, body }) => ({ url: `/customers/${id}`, method: 'PATCH', body }),
      transformResponse: unwrap<Customer>,
      invalidatesTags: ['Customer'],
    }),
    deleteCustomer: builder.mutation<void, string>({
      query: (id) => ({ url: `/customers/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Customer'],
    }),
    customerStatement: builder.query<
      ListResult<StatementEntry> & { customer: Partial<Customer> },
      { id: string; params?: ListQuery }
    >({
      query: ({ id, params }) => ({
        url: `/customers/${id}/statement`,
        params: listParams(params ?? {}),
      }),
      transformResponse: (response: {
        success: true;
        data: StatementEntry[];
        meta: ListResult<StatementEntry>['meta'];
        customer: Partial<Customer>;
      }) => ({ items: response.data, meta: response.meta, customer: response.customer }),
      providesTags: ['CustomerStatement'],
    }),
    adjustCustomerBalance: builder.mutation<
      Customer,
      { id: string; amount: number; reason: string }
    >({
      query: ({ id, ...body }) => ({ url: `/customers/${id}/adjust`, method: 'POST', body }),
      transformResponse: unwrap<Customer>,
      invalidatesTags: ['Customer', 'CustomerStatement'],
    }),
    customerGroups: builder.query<CustomerGroup[], void>({
      query: () => ({ url: '/customers/groups' }),
      transformResponse: unwrap<CustomerGroup[]>,
      providesTags: ['CustomerGroup'],
    }),
    createCustomerGroup: builder.mutation<CustomerGroup, Record<string, unknown>>({
      query: (body) => ({ url: '/customers/groups', method: 'POST', body }),
      transformResponse: unwrap<CustomerGroup>,
      invalidatesTags: ['CustomerGroup'],
    }),

    // --- sales ---
    sales: builder.query<SummarisedList<Sale, MoneySummary>, ListQuery | void>({
      query: (params) => ({ url: '/sales', params: listParams(params ?? {}) }),
      transformResponse: (response: { data: Sale[]; meta: ListResult<Sale>['meta']; summary: MoneySummary }) => ({
        items: response.data, meta: response.meta, summary: response.summary,
      }),
      providesTags: ['Sale'],
    }),
    sale: builder.query<Sale, string>({
      query: (id) => ({ url: `/sales/${id}` }),
      transformResponse: unwrap<Sale>,
      providesTags: ['Sale'],
    }),
    hideSale: builder.mutation<Sale, { id: string; reason?: string }>({
      query: ({ id, reason }) => ({ url: `/sales/${id}`, method: 'DELETE', body: { reason } }),
      transformResponse: unwrap<Sale>,
      invalidatesTags: ['Sale', 'Payment', 'Account', 'Stock', 'StockLedger', 'Customer', 'CustomerStatement'],
    }),
    restoreSale: builder.mutation<Sale, string>({
      query: (id) => ({ url: `/sales/${id}/restore`, method: 'POST' }),
      transformResponse: unwrap<Sale>,
      invalidatesTags: ['Sale', 'Payment', 'Account', 'Stock', 'StockLedger', 'Customer', 'CustomerStatement'],
    }),
    createSale: builder.mutation<Sale, Record<string, unknown>>({
      query: (body) => ({ url: '/sales', method: 'POST', body }),
      transformResponse: unwrap<Sale>,
      invalidatesTags: ['Sale'],
    }),
    updateSale: builder.mutation<Sale, { id: string; body: Record<string, unknown> }>({
      query: ({ id, body }) => ({ url: `/sales/${id}`, method: 'PATCH', body }),
      transformResponse: unwrap<Sale>,
      invalidatesTags: ['Sale'],
    }),
    /** confirm · cancel */
    saleAction: builder.mutation<
      Sale,
      { id: string; action: string; body?: Record<string, unknown> }
    >({
      query: ({ id, action, body }) => ({
        url: `/sales/${id}/${action}`, method: 'POST', body: body ?? {},
      }),
      transformResponse: unwrap<Sale>,
      // Confirming moves stock and the customer balance.
      invalidatesTags: ['Sale', 'Stock', 'StockLedger', 'Customer', 'CustomerStatement'],
    }),

    // --- payments ---
    payments: builder.query<SummarisedList<Payment, MoneySummary>, ListQuery | void>({
      query: (params) => ({ url: '/payments', params: listParams(params ?? {}) }),
      transformResponse: (response: { data: Payment[]; meta: ListResult<Payment>['meta']; summary: MoneySummary }) => ({
        items: response.data, meta: response.meta, summary: response.summary,
      }),
      providesTags: ['Payment'],
    }),
    accounts: builder.query<ListResult<FinancialAccount>, ListQuery | void>({
      query: (params) => ({ url: '/accounts', params: listParams(params ?? {}) }),
      transformResponse: unwrapList<FinancialAccount>,
      providesTags: ['Account'],
    }),
    account: builder.query<FinancialAccount, string>({
      query: (id) => ({ url: `/accounts/${id}` }),
      transformResponse: unwrap<FinancialAccount>,
      providesTags: ['Account'],
    }),
    createAccount: builder.mutation<FinancialAccount, Record<string, unknown>>({
      query: (body) => ({ url: '/accounts', method: 'POST', body }),
      transformResponse: unwrap<FinancialAccount>,
      invalidatesTags: ['Account', 'AccountTransaction'],
    }),
    updateAccount: builder.mutation<FinancialAccount, { id: string; body: Record<string, unknown> }>({
      query: ({ id, body }) => ({ url: `/accounts/${id}`, method: 'PATCH', body }),
      transformResponse: unwrap<FinancialAccount>,
      invalidatesTags: ['Account'],
    }),
    moveAccountCash: builder.mutation<
      { accountId: string; amount: string; balance: string },
      { id: string; direction: 'deposit' | 'withdraw'; amount: number; reason: string; reference?: string }
    >({
      query: ({ id, direction, ...body }) => ({
        url: `/accounts/${id}/${direction}`, method: 'POST', body,
      }),
      transformResponse: unwrap<{ accountId: string; amount: string; balance: string }>,
      invalidatesTags: ['Account', 'AccountTransaction'],
    }),
    deleteAccount: builder.mutation<{ id: string; deleted: boolean }, string>({
      query: (id) => ({ url: `/accounts/${id}`, method: 'DELETE' }),
      transformResponse: unwrap<{ id: string; deleted: boolean }>,
      invalidatesTags: ['Account', 'PaymentMethod'],
    }),
    adjustAccount: builder.mutation<
      { accountId: string; difference: string; balance: string },
      { id: string; countedBalance: number; reason: string }
    >({
      query: ({ id, ...body }) => ({ url: `/accounts/${id}/adjust`, method: 'POST', body }),
      transformResponse: unwrap<{ accountId: string; difference: string; balance: string }>,
      invalidatesTags: ['Account', 'AccountTransaction'],
    }),
    accountTransactions: builder.query<ListResult<AccountTransaction>, ListQuery | void>({
      query: (params) => ({ url: '/accounts/transactions', params: listParams(params ?? {}) }),
      transformResponse: unwrapList<AccountTransaction>,
      providesTags: ['AccountTransaction'],
    }),
    dailyBalance: builder.query<DailyBalance, { date?: string } | void>({
      query: (params) => ({ url: '/accounts/daily-balance', params: listParams(params ?? {}) }),
      transformResponse: unwrap<DailyBalance>,
      providesTags: ['AccountTransaction'],
    }),

    createPaymentMethod: builder.mutation<PaymentMethod, Record<string, unknown>>({
      query: (body) => ({ url: '/payments/methods', method: 'POST', body }),
      transformResponse: unwrap<PaymentMethod>,
      invalidatesTags: ['PaymentMethod', 'Account'],
    }),
    updatePaymentMethod: builder.mutation<
      PaymentMethod,
      { id: string; body: Record<string, unknown> }
    >({
      query: ({ id, body }) => ({ url: `/payments/methods/${id}`, method: 'PATCH', body }),
      transformResponse: unwrap<PaymentMethod>,
      invalidatesTags: ['PaymentMethod', 'Account'],
    }),
    paymentMethods: builder.query<PaymentMethod[], void>({
      query: () => ({ url: '/payments/methods' }),
      transformResponse: unwrap<PaymentMethod[]>,
      providesTags: ['PaymentMethod'],
    }),
    createPayment: builder.mutation<Payment, Record<string, unknown>>({
      query: (body) => ({ url: '/payments', method: 'POST', body }),
      transformResponse: unwrap<Payment>,
      invalidatesTags: ['Payment', 'Sale', 'Customer', 'CustomerStatement', 'Invoice'],
    }),
    voidPayment: builder.mutation<Payment, { id: string; reason: string }>({
      query: ({ id, reason }) => ({ url: `/payments/${id}/void`, method: 'POST', body: { reason } }),
      transformResponse: unwrap<Payment>,
      invalidatesTags: ['Payment', 'Sale', 'Customer', 'CustomerStatement', 'Invoice'],
    }),

    // --- returns ---
    salesReturns: builder.query<SummarisedList<SalesReturn, MoneySummary>, ListQuery | void>({
      query: (params) => ({ url: '/returns', params: listParams(params ?? {}) }),
      transformResponse: (response: { data: SalesReturn[]; meta: ListResult<SalesReturn>['meta']; summary: MoneySummary }) => ({
        items: response.data, meta: response.meta, summary: response.summary,
      }),
      providesTags: ['SalesReturn'],
    }),
    uncollectedSales: builder.query<ListResult<UncollectedSale>, ListQuery | void>({
      query: (params) => ({ url: '/sales/uncollected', params: listParams(params ?? {}) }),
      transformResponse: unwrapList<UncollectedSale>,
      providesTags: ['Sale'],
    }),
    collectSale: builder.mutation<
      Sale,
      { id: string; lines?: { saleItemId: string; quantity: number }[]; notes?: string }
    >({
      query: ({ id, ...body }) => ({ url: `/sales/${id}/collect`, method: 'POST', body }),
      transformResponse: unwrap<Sale>,
      invalidatesTags: ['Sale'],
    }),
    cancelUncollected: builder.mutation<
      SalesReturn & { cashRefunded: string },
      {
        saleId: string;
        refund: 'CASH' | 'CREDIT';
        lines?: { saleItemId: string; quantity: number }[];
        reason?: string;
      }
    >({
      query: ({ saleId, ...body }) => ({ url: `/returns/uncollected/${saleId}/cancel`, method: 'POST', body }),
      transformResponse: unwrap<SalesReturn & { cashRefunded: string }>,
      invalidatesTags: ['Sale', 'SalesReturn', 'Stock', 'StockLedger', 'Account', 'Customer', 'CustomerStatement', 'Payment', 'Product'],
    }),
    deletedSales: builder.query<ListResult<DeletedSale>, ListQuery | void>({
      query: (params) => ({ url: '/returns/deleted-sales', params: listParams(params ?? {}) }),
      transformResponse: unwrapList<DeletedSale>,
      providesTags: ['Sale'],
    }),
    salesReturn: builder.query<SalesReturn, string>({
      query: (id) => ({ url: `/returns/${id}` }),
      transformResponse: unwrap<SalesReturn>,
      providesTags: ['SalesReturn'],
    }),
    createSalesReturn: builder.mutation<SalesReturn, Record<string, unknown>>({
      query: (body) => ({ url: '/returns', method: 'POST', body }),
      transformResponse: unwrap<SalesReturn>,
      invalidatesTags: ['SalesReturn'],
    }),
    /** submit · approve · reject · cancel */
    salesReturnAction: builder.mutation<
      SalesReturn,
      { id: string; action: string; body?: Record<string, unknown> }
    >({
      query: ({ id, action, body }) => ({
        url: `/returns/${id}/${action}`, method: 'POST', body: body ?? {},
      }),
      transformResponse: unwrap<SalesReturn>,
      // Approval restocks goods and credits the customer.
      invalidatesTags: [
        'SalesReturn', 'Stock', 'StockLedger', 'Customer', 'CustomerStatement', 'Sale',
      ],
    }),

    // --- invoices ---
    invoices: builder.query<SummarisedList<Invoice, MoneySummary>, ListQuery | void>({
      query: (params) => ({ url: '/invoices', params: listParams(params ?? {}) }),
      transformResponse: (response: { data: Invoice[]; meta: ListResult<Invoice>['meta']; summary: MoneySummary }) => ({
        items: response.data, meta: response.meta, summary: response.summary,
      }),
      providesTags: ['Invoice'],
    }),
    invoice: builder.query<Invoice, string>({
      query: (id) => ({ url: `/invoices/${id}` }),
      transformResponse: unwrap<Invoice>,
      providesTags: ['Invoice'],
    }),
    issueInvoice: builder.mutation<Invoice, Record<string, unknown>>({
      query: (body) => ({ url: '/invoices', method: 'POST', body }),
      transformResponse: unwrap<Invoice>,
      invalidatesTags: ['Invoice', 'Sale'],
    }),
    /** refresh · void */
    invoiceAction: builder.mutation<
      Invoice,
      { id: string; action: string; body?: Record<string, unknown> }
    >({
      query: ({ id, action, body }) => ({
        url: `/invoices/${id}/${action}`, method: 'POST', body: body ?? {},
      }),
      transformResponse: unwrap<Invoice>,
      invalidatesTags: ['Invoice'],
    }),

    // --- point of sale ---
    posSessions: builder.query<ListResult<PosSession>, ListQuery | void>({
      query: (params) => ({ url: '/pos/sessions', params: listParams(params ?? {}) }),
      transformResponse: unwrapList<PosSession>,
      providesTags: ['PosSession'],
    }),
    currentPosSession: builder.query<PosSession | null, void>({
      query: () => ({ url: '/pos/sessions/current' }),
      transformResponse: unwrap<PosSession | null>,
      providesTags: ['PosSession'],
    }),
    openPosSession: builder.mutation<PosSession, Record<string, unknown>>({
      query: (body) => ({ url: '/pos/sessions', method: 'POST', body }),
      transformResponse: unwrap<PosSession>,
      invalidatesTags: ['PosSession'],
    }),
    /** close · reconcile */
    posSessionAction: builder.mutation<
      PosSession,
      { id: string; action: string; body?: Record<string, unknown> }
    >({
      query: ({ id, action, body }) => ({
        url: `/pos/sessions/${id}/${action}`, method: 'POST', body: body ?? {},
      }),
      transformResponse: unwrap<PosSession>,
      invalidatesTags: ['PosSession'],
    }),
    posCheckout: builder.mutation<PosCheckoutResult, Record<string, unknown>>({
      query: (body) => ({ url: '/pos/checkout', method: 'POST', body }),
      transformResponse: unwrap<PosCheckoutResult>,
      invalidatesTags: [
        'PosSession', 'Sale', 'Stock', 'StockLedger', 'Payment', 'Customer', 'CustomerStatement',
      ],
    }),
    heldCarts: builder.query<HeldCart[], void>({
      query: () => ({ url: '/pos/held-carts' }),
      transformResponse: unwrap<HeldCart[]>,
      providesTags: ['HeldCart'],
    }),
    holdCart: builder.mutation<HeldCart, Record<string, unknown>>({
      query: (body) => ({ url: '/pos/held-carts', method: 'POST', body }),
      transformResponse: unwrap<HeldCart>,
      invalidatesTags: ['HeldCart', 'PosSession'],
    }),
    releaseHeldCart: builder.mutation<void, string>({
      query: (id) => ({ url: `/pos/held-carts/${id}`, method: 'DELETE' }),
      invalidatesTags: ['HeldCart', 'PosSession'],
    }),

    // --- catalog ---
    categories: builder.query<ListResult<Category>, ListQuery | void>({
      query: (params) => ({ url: '/categories', params: listParams(params ?? {}) }),
      transformResponse: unwrapList<Category>,
      providesTags: ['Category'],
    }),
    categoryTree: builder.query<CategoryNode[], void>({
      query: () => ({ url: '/categories/tree' }),
      transformResponse: unwrap<CategoryNode[]>,
      providesTags: ['Category'],
    }),
    createCategory: builder.mutation<Category, Partial<Category> & { name: string }>({
      query: (body) => ({ url: '/categories', method: 'POST', body }),
      transformResponse: unwrap<Category>,
      invalidatesTags: ['Category'],
    }),
    updateCategory: builder.mutation<Category, { id: string; body: Partial<Category> }>({
      query: ({ id, body }) => ({ url: `/categories/${id}`, method: 'PATCH', body }),
      transformResponse: unwrap<Category>,
      invalidatesTags: ['Category'],
    }),
    deleteCategory: builder.mutation<void, string>({
      query: (id) => ({ url: `/categories/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Category'],
    }),

    brands: builder.query<ListResult<Brand>, ListQuery | void>({
      query: (params) => ({ url: '/brands', params: listParams(params ?? {}) }),
      transformResponse: unwrapList<Brand>,
      providesTags: ['Brand'],
    }),
    createBrand: builder.mutation<Brand, { name: string; description?: string }>({
      query: (body) => ({ url: '/brands', method: 'POST', body }),
      transformResponse: unwrap<Brand>,
      invalidatesTags: ['Brand'],
    }),
    deleteBrand: builder.mutation<void, string>({
      query: (id) => ({ url: `/brands/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Brand'],
    }),

    units: builder.query<ListResult<Unit>, ListQuery | void>({
      query: (params) => ({ url: '/units', params: listParams(params ?? {}) }),
      transformResponse: unwrapList<Unit>,
      providesTags: ['Unit'],
    }),
    createUnit: builder.mutation<Unit, Partial<Unit> & { name: string; symbol: string }>({
      query: (body) => ({ url: '/units', method: 'POST', body }),
      transformResponse: unwrap<Unit>,
      invalidatesTags: ['Unit'],
    }),
    deleteUnit: builder.mutation<void, string>({
      query: (id) => ({ url: `/units/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Unit'],
    }),
    unitConversions: builder.query<ListResult<UnitConversion>, ListQuery | void>({
      query: (params) => ({ url: '/units/conversions', params: listParams(params ?? {}) }),
      transformResponse: unwrapList<UnitConversion>,
      providesTags: ['UnitConversion'],
    }),
    createUnitConversion: builder.mutation<
      UnitConversion,
      { fromUnitId: string; toUnitId: string; factor: number; productId?: string }
    >({
      query: (body) => ({ url: '/units/conversions', method: 'POST', body }),
      transformResponse: unwrap<UnitConversion>,
      invalidatesTags: ['UnitConversion'],
    }),
    deleteUnitConversion: builder.mutation<void, string>({
      query: (id) => ({ url: `/units/conversions/${id}`, method: 'DELETE' }),
      invalidatesTags: ['UnitConversion'],
    }),

    products: builder.query<SummarisedList<Product, MoneySummary>, ListQuery | void>({
      query: (params) => ({ url: '/products', params: listParams(params ?? {}) }),
      transformResponse: (response: { data: Product[]; meta: ListResult<Product>['meta']; summary: MoneySummary }) => ({
        items: response.data, meta: response.meta, summary: response.summary,
      }),
      providesTags: ['Product'],
    }),
    productPages: builder.infiniteQuery<ListResult<Product>, ListQuery, number>({
      infiniteQueryOptions: pagedListOptions,
      query: ({ queryArg, pageParam }) => ({ url: '/products', params: pickerParams(queryArg, pageParam) }),
      transformResponse: (response: { data: Product[]; meta: ListResult<Product>['meta'] }) => ({
        items: response.data, meta: response.meta,
      }),
      providesTags: ['Product'],
    }),
    product: builder.query<Product, string>({
      query: (id) => ({ url: `/products/${id}` }),
      transformResponse: unwrap<Product>,
      providesTags: ['Product'],
    }),
    createProduct: builder.mutation<Product, Record<string, unknown>>({
      query: (body) => ({ url: '/products', method: 'POST', body }),
      transformResponse: unwrap<Product>,
      invalidatesTags: ['Product'],
    }),
    updateProduct: builder.mutation<Product, { id: string; body: Record<string, unknown> }>({
      query: ({ id, body }) => ({ url: `/products/${id}`, method: 'PATCH', body }),
      transformResponse: unwrap<Product>,
      invalidatesTags: ['Product'],
    }),
    deleteProduct: builder.mutation<void, string>({
      query: (id) => ({ url: `/products/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Product'],
    }),
    addProductVariant: builder.mutation<
      ProductVariant,
      { productId: string; body: Record<string, unknown> }
    >({
      query: ({ productId, body }) => ({
        url: `/products/${productId}/variants`, method: 'POST', body,
      }),
      transformResponse: unwrap<ProductVariant>,
      invalidatesTags: ['Product'],
    }),
    updateProductVariant: builder.mutation<
      ProductVariant,
      { productId: string; variantId: string; body: Record<string, unknown> }
    >({
      query: ({ productId, variantId, body }) => ({
        url: `/products/${productId}/variants/${variantId}`, method: 'PATCH', body,
      }),
      transformResponse: unwrap<ProductVariant>,
      invalidatesTags: ['Product'],
    }),
    deleteProductVariant: builder.mutation<void, { productId: string; variantId: string }>({
      query: ({ productId, variantId }) => ({
        url: `/products/${productId}/variants/${variantId}`, method: 'DELETE',
      }),
      invalidatesTags: ['Product'],
    }),
    addProductUnit: builder.mutation<
      ProductUnitRow,
      { productId: string; body: { unitId: string; factor: number; isPurchaseDefault?: boolean } }
    >({
      query: ({ productId, body }) => ({
        url: `/products/${productId}/units`, method: 'POST', body,
      }),
      transformResponse: unwrap<ProductUnitRow>,
      invalidatesTags: ['Product'],
    }),
    deleteProductUnit: builder.mutation<void, { productId: string; productUnitId: string }>({
      query: ({ productId, productUnitId }) => ({
        url: `/products/${productId}/units/${productUnitId}`, method: 'DELETE',
      }),
      invalidatesTags: ['Product'],
    }),

    // --- transfers ---
    transfers: builder.query<ListResult<Transfer>, ListQuery | void>({
      query: (params) => ({ url: '/transfers', params: listParams(params ?? {}) }),
      transformResponse: unwrapList<Transfer>,
      providesTags: ['Transfer'],
    }),
    transfer: builder.query<Transfer, string>({
      query: (id) => ({ url: `/transfers/${id}` }),
      transformResponse: unwrap<Transfer>,
      providesTags: ['Transfer'],
    }),
    createTransfer: builder.mutation<Transfer, Record<string, unknown>>({
      query: (body) => ({ url: '/transfers', method: 'POST', body }),
      transformResponse: unwrap<Transfer>,
      invalidatesTags: ['Transfer'],
    }),
    /** submit · approve · reject · dispatch · receive · cancel */
    transferAction: builder.mutation<
      Transfer,
      { id: string; action: string; body?: Record<string, unknown> }
    >({
      query: ({ id, action, body }) => ({
        url: `/transfers/${id}/${action}`, method: 'POST', body: body ?? {},
      }),
      transformResponse: unwrap<Transfer>,
      // Dispatch and receive move stock, so the balances are stale too.
      invalidatesTags: ['Transfer', 'Stock', 'StockLedger'],
    }),

    // --- stock counts ---
    stockCounts: builder.query<ListResult<StockCount>, ListQuery | void>({
      query: (params) => ({ url: '/stock-counts', params: listParams(params ?? {}) }),
      transformResponse: unwrapList<StockCount>,
      providesTags: ['StockCount'],
    }),
    stockCount: builder.query<StockCount, string>({
      query: (id) => ({ url: `/stock-counts/${id}` }),
      transformResponse: unwrap<StockCount>,
      providesTags: ['StockCount'],
    }),
    stockCountItems: builder.query<
      ListResult<StockCountItemRow>,
      { id: string; params?: ListQuery }
    >({
      query: ({ id, params }) => ({
        url: `/stock-counts/${id}/items`, params: listParams(params ?? {}),
      }),
      transformResponse: unwrapList<StockCountItemRow>,
      providesTags: ['StockCount'],
    }),
    createStockCount: builder.mutation<StockCount, Record<string, unknown>>({
      query: (body) => ({ url: '/stock-counts', method: 'POST', body }),
      transformResponse: unwrap<StockCount>,
      invalidatesTags: ['StockCount'],
    }),
    recordStockCounts: builder.mutation<
      StockCount,
      { id: string; lines: { itemId: string; countedQty: number; notes?: string }[] }
    >({
      query: ({ id, lines }) => ({
        url: `/stock-counts/${id}/items`, method: 'PATCH', body: { lines },
      }),
      transformResponse: unwrap<StockCount>,
      invalidatesTags: ['StockCount'],
    }),
    /** submit · approve · cancel */
    stockCountAction: builder.mutation<
      StockCount,
      { id: string; action: string; body?: Record<string, unknown> }
    >({
      query: ({ id, action, body }) => ({
        url: `/stock-counts/${id}/${action}`, method: 'POST', body: body ?? {},
      }),
      transformResponse: unwrap<StockCount>,
      // Approval posts every variance to the ledger.
      invalidatesTags: ['StockCount', 'Stock', 'StockLedger'],
    }),

    stock: builder.query<SummarisedList<StockRow, MoneySummary>, ListQuery | void>({
      query: (params) => ({ url: '/stock', params: listParams(params ?? {}) }),
      transformResponse: (response: { data: StockRow[]; meta: ListResult<StockRow>['meta']; summary: MoneySummary }) => ({
        items: response.data, meta: response.meta, summary: response.summary,
      }),
      providesTags: ['Stock'],
    }),
    stockLedger: builder.query<ListResult<LedgerEntry>, ListQuery | void>({
      query: (params) => ({ url: '/stock/ledger', params: listParams(params ?? {}) }),
      transformResponse: unwrapList<LedgerEntry>,
      providesTags: ['StockLedger'],
    }),
    salesReport: builder.query<SalesReport, { from: string; to: string; customerId?: string }>({
      query: (params) => ({ url: '/reports/sales', params: listParams(params) }),
      transformResponse: unwrap<SalesReport>,
      providesTags: ['Sale'],
    }),
    properties: builder.query<ListResult<Property>, ListQuery | void>({
      query: (params) => ({ url: '/properties', params: listParams(params ?? {}) }),
      transformResponse: unwrapList<Property>,
      providesTags: ['Property'],
    }),
    property: builder.query<Property, string>({
      query: (id) => ({ url: `/properties/${id}` }),
      transformResponse: unwrap<Property>,
      providesTags: ['Property'],
    }),
    propertySummary: builder.query<PropertySummary, void>({
      query: () => ({ url: '/properties/summary' }),
      transformResponse: unwrap<PropertySummary>,
      providesTags: ['Property', 'PropertySale'],
    }),
    createProperty: builder.mutation<Property, Record<string, unknown>>({
      query: (body) => ({ url: '/properties', method: 'POST', body }),
      transformResponse: unwrap<Property>,
      invalidatesTags: ['Property'],
    }),
    updateProperty: builder.mutation<Property, { id: string; body: Record<string, unknown> }>({
      query: ({ id, body }) => ({ url: `/properties/${id}`, method: 'PATCH', body }),
      transformResponse: unwrap<Property>,
      invalidatesTags: ['Property'],
    }),
    withdrawProperty: builder.mutation<{ id: string }, string>({
      query: (id) => ({ url: `/properties/${id}`, method: 'DELETE' }),
      transformResponse: unwrap<{ id: string }>,
      invalidatesTags: ['Property'],
    }),
    profitAndLoss: builder.query<ProfitAndLoss, { from: string; to: string; branchId?: string }>({
      query: (params) => ({ url: '/reports/profit-and-loss', params }),
      transformResponse: unwrap<ProfitAndLoss>,
      providesTags: ['Sale', 'Expense', 'PropertySale'],
    }),
    cashFlow: builder.query<CashFlowReport, { from: string; to: string }>({
      query: (params) => ({ url: '/reports/cash-flow', params }),
      transformResponse: unwrap<CashFlowReport>,
      providesTags: ['Account', 'AccountTransaction'],
    }),
    position: builder.query<PositionReport, { asAt?: string } | void>({
      query: (params) => ({ url: '/reports/position', params: params ?? {} }),
      transformResponse: unwrap<PositionReport>,
      providesTags: ['Account', 'Stock', 'Sale', 'Property'],
    }),
    propertySales: builder.query<PropertySalesResult, ListQuery | void>({
      query: (params) => ({ url: '/properties/sales', params: listParams(params ?? {}) }),
      transformResponse: (response: {
        data: PropertySale[];
        meta: ListResult<PropertySale>['meta'];
        summary: PropertySalesResult['summary'];
      }) => ({ items: response.data, meta: response.meta, summary: response.summary }),
      providesTags: ['PropertySale'],
    }),
    propertySale: builder.query<PropertySale, string>({
      query: (id) => ({ url: `/properties/sales/${id}` }),
      transformResponse: unwrap<PropertySale>,
      providesTags: ['PropertySale'],
    }),
    createPropertySale: builder.mutation<PropertySale, Record<string, unknown>>({
      query: (body) => ({ url: '/properties/sales', method: 'POST', body }),
      transformResponse: unwrap<PropertySale>,
      invalidatesTags: ['PropertySale', 'Property', 'Account', 'AccountTransaction'],
    }),
    updatePropertySale: builder.mutation<
      PropertySale,
      { saleId: string; body: Record<string, unknown> }
    >({
      query: ({ saleId, body }) => ({ url: `/properties/sales/${saleId}`, method: 'PATCH', body }),
      transformResponse: unwrap<PropertySale>,
      invalidatesTags: ['PropertySale', 'Property'],
    }),
    hidePropertySale: builder.mutation<
      { id: string; hidden: boolean; cashEntriesReversed: number },
      { saleId: string; reason: string }
    >({
      query: ({ saleId, reason }) => ({
        url: `/properties/sales/${saleId}`,
        method: 'DELETE',
        body: { reason },
      }),
      transformResponse: unwrap<{ id: string; hidden: boolean; cashEntriesReversed: number }>,
      invalidatesTags: ['PropertySale', 'Property', 'Account', 'AccountTransaction'],
    }),
    restorePropertySale: builder.mutation<PropertySale, string>({
      query: (saleId) => ({ url: `/properties/sales/${saleId}/restore`, method: 'POST' }),
      transformResponse: unwrap<PropertySale>,
      invalidatesTags: ['PropertySale', 'Property', 'Account', 'AccountTransaction'],
    }),
    voidPropertyPayment: builder.mutation<
      PropertySale,
      { saleId: string; paymentId: string; reason: string }
    >({
      query: ({ saleId, paymentId, reason }) => ({
        url: `/properties/sales/${saleId}/payments/${paymentId}`,
        method: 'DELETE',
        body: { reason },
      }),
      transformResponse: unwrap<PropertySale>,
      invalidatesTags: ['PropertySale', 'Property', 'Account', 'AccountTransaction'],
    }),
    runPropertyReminders: builder.mutation<{ sales: number; emails: number }, void>({
      query: () => ({ url: '/properties/reminders/run', method: 'POST' }),
      transformResponse: unwrap<{ sales: number; emails: number }>,
      invalidatesTags: ['PropertySale', 'Notification'],
    }),
    recordPropertyPayment: builder.mutation<
      PropertySale,
      { saleId: string; body: Record<string, unknown> }
    >({
      query: ({ saleId, body }) => ({
        url: `/properties/sales/${saleId}/payments`, method: 'POST', body,
      }),
      transformResponse: unwrap<PropertySale>,
      invalidatesTags: ['PropertySale', 'Property', 'Account', 'AccountTransaction'],
    }),
    cancelPropertySale: builder.mutation<PropertySale, { saleId: string; reason: string }>({
      query: ({ saleId, reason }) => ({
        url: `/properties/sales/${saleId}/cancel`, method: 'POST', body: { reason },
      }),
      transformResponse: unwrap<PropertySale>,
      invalidatesTags: ['PropertySale', 'Property'],
    }),
    propertyReceipt: builder.query<Record<string, unknown>, { saleId: string; paymentId?: string }>({
      query: ({ saleId, paymentId }) => ({
        url: `/properties/sales/${saleId}/receipt`,
        params: paymentId ? { paymentId } : undefined,
      }),
      transformResponse: unwrap<Record<string, unknown>>,
      providesTags: ['PropertySale'],
    }),

    stockValuation: builder.query<StockValuation, { warehouseId?: string } | void>({
      query: (params) => ({ url: '/stock/valuation', params: listParams(params ?? {}) }),
      transformResponse: unwrap<StockValuation>,
      providesTags: ['Stock'],
    }),
    adjustStock: builder.mutation<
      { reference: string },
      {
        warehouseId: string;
        reason: string;
        notes?: string;
        lines: {
          variantId: string;
          quantity: number;
          unitId?: string;
          unitCost?: number;
          notes?: string;
        }[];
      }
    >({
      query: (body) => ({ url: '/stock/adjustments', method: 'POST', body }),
      transformResponse: unwrap<{ reference: string }>,
      invalidatesTags: ['Stock', 'StockLedger'],
    }),

    // --- audit & notifications ---
    auditLogs: builder.query<ListResult<AuditEntry>, ListQuery | void>({
      query: (params) => ({ url: '/audit-logs', params: listParams(params ?? {}) }),
      transformResponse: unwrapList<AuditEntry>,
      providesTags: ['Audit'],
    }),
    notifications: builder.query<ListResult<NotificationItem>, ListQuery | void>({
      query: (params) => ({ url: '/notifications', params: listParams(params ?? {}) }),
      transformResponse: unwrapList<NotificationItem>,
      providesTags: ['Notification'],
    }),
    markNotificationRead: builder.mutation<unknown, string>({
      query: (id) => ({ url: `/notifications/${id}/read`, method: 'PATCH' }),
      invalidatesTags: ['Notification'],
    }),
  }),
});

export const {
  useLoginMutation,
  useRegisterMutation,
  useLogoutMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useChangePasswordMutation,
  useVerifyEmailMutation,
  useMeQuery,
  useLazyMeQuery,
  useUpdateProfileMutation,
  useSessionsQuery,
  useRevokeSessionMutation,
  useBusinessTypesQuery,
  useCreateOrganizationMutation,
  useOrganizationContextQuery,
  useOrganizationQuery,
  useUpdateOrganizationMutation,
  useSettingsQuery,
  useUpdateSettingsMutation,
  useFeaturesQuery,
  useToggleFeatureMutation,
  useCompleteOnboardingMutation,
  useSubscriptionQuery,
  usePlansQuery,
  useRenewSubscriptionMutation,
  useBranchesQuery,
  useCreateBranchMutation,
  useUpdateBranchMutation,
  useDeleteBranchMutation,
  useWarehousesQuery,
  useCreateWarehouseMutation,
  useUpdateWarehouseMutation,
  useDeleteWarehouseMutation,
  useMembersQuery,
  useInviteMemberMutation,
  useCreateMemberMutation,
  useSetMemberPasswordMutation,
  useUpdateMemberMutation,
  useRemoveMemberMutation,
  useRolesQuery,
  usePermissionCatalogueQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useSuppliersQuery,
  useSupplierPagesInfiniteQuery,
  useSupplierQuery,
  useCreateSupplierMutation,
  useUpdateSupplierMutation,
  useDeleteSupplierMutation,
  useSupplierStatementQuery,
  usePurchaseOrdersQuery,
  usePurchaseOrderQuery,
  useCreatePurchaseOrderMutation,
  usePurchaseOrderActionMutation,
  useGoodsReceiptsQuery,
  useGoodsReceiptQuery,
  useCreateGoodsReceiptMutation,
  useGoodsReceiptActionMutation,
  useSupplierInvoicesQuery,
  useCreateSupplierInvoiceMutation,
  useVoidSupplierInvoiceMutation,
  usePaySupplierMutation,
  useSupplierReturnsQuery,
  useCreateSupplierReturnMutation,
  useSupplierReturnActionMutation,
  useExpensesQuery,
  useCreateExpenseMutation,
  useUpdateExpenseMutation,
  useDeleteExpenseMutation,
  useRestoreExpenseMutation,
  useExpenseActionMutation,
  useExpenseCategoriesQuery,
  useCreateExpenseCategoryMutation,
  useCustomersQuery,
  useCustomerPagesInfiniteQuery,
  useCustomerQuery,
  useCustomerDepositsQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useDeleteCustomerMutation,
  useCustomerStatementQuery,
  useAdjustCustomerBalanceMutation,
  useCustomerGroupsQuery,
  useCreateCustomerGroupMutation,
  useSalesQuery,
  useSaleQuery,
  useAccountsQuery,
  useSalesReportQuery,
  useProfitAndLossQuery,
  useCashFlowQuery,
  usePositionQuery,
  usePropertiesQuery,
  usePropertyQuery,
  usePropertySummaryQuery,
  useCreatePropertyMutation,
  useUpdatePropertyMutation,
  useWithdrawPropertyMutation,
  usePropertySalesQuery,
  usePropertySaleQuery,
  useCreatePropertySaleMutation,
  useRecordPropertyPaymentMutation,
  useRunPropertyRemindersMutation,
  useHidePropertySaleMutation,
  useRestorePropertySaleMutation,
  useVoidPropertyPaymentMutation,
  useUpdatePropertySaleMutation,
  useCancelPropertySaleMutation,
  usePropertyReceiptQuery,
  useCreatePaymentMethodMutation,
  useUpdatePaymentMethodMutation,
  useAccountQuery,
  useCreateAccountMutation,
  useUpdateAccountMutation,
  useAdjustAccountMutation,
  useDeleteAccountMutation,
  useMoveAccountCashMutation,
  useAccountTransactionsQuery,
  useDailyBalanceQuery,
  useCreateSaleMutation,
  useHideSaleMutation,
  useRestoreSaleMutation,
  useUpdateSaleMutation,
  useSaleActionMutation,
  usePaymentsQuery,
  usePaymentMethodsQuery,
  useCreatePaymentMutation,
  useVoidPaymentMutation,
  useSalesReturnsQuery,
  useDeletedSalesQuery,
  useUncollectedSalesQuery,
  useCollectSaleMutation,
  useCancelUncollectedMutation,
  useSalesReturnQuery,
  useCreateSalesReturnMutation,
  useSalesReturnActionMutation,
  useInvoicesQuery,
  useInvoiceQuery,
  useIssueInvoiceMutation,
  useInvoiceActionMutation,
  usePosSessionsQuery,
  useCurrentPosSessionQuery,
  useOpenPosSessionMutation,
  usePosSessionActionMutation,
  usePosCheckoutMutation,
  useHeldCartsQuery,
  useHoldCartMutation,
  useReleaseHeldCartMutation,
  useCategoriesQuery,
  useCategoryTreeQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useBrandsQuery,
  useCreateBrandMutation,
  useDeleteBrandMutation,
  useUnitsQuery,
  useCreateUnitMutation,
  useDeleteUnitMutation,
  useUnitConversionsQuery,
  useCreateUnitConversionMutation,
  useDeleteUnitConversionMutation,
  useProductsQuery,
  useProductPagesInfiniteQuery,
  useProductQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  useAddProductVariantMutation,
  useUpdateProductVariantMutation,
  useDeleteProductVariantMutation,
  useAddProductUnitMutation,
  useDeleteProductUnitMutation,
  useTransfersQuery,
  useTransferQuery,
  useCreateTransferMutation,
  useTransferActionMutation,
  useStockCountsQuery,
  useStockCountQuery,
  useStockCountItemsQuery,
  useCreateStockCountMutation,
  useRecordStockCountsMutation,
  useStockCountActionMutation,
  useStockQuery,
  useStockLedgerQuery,
  useStockValuationQuery,
  useAdjustStockMutation,
  useAuditLogsQuery,
  useNotificationsQuery,
  useMarkNotificationReadMutation,
} = api;

export type { AuthTokens };
